import { Component, ElementRef, Input, ViewChild, ViewEncapsulation, OnInit, OnDestroy, OnChanges, AfterViewInit, ChangeDetectorRef, HostListener, SimpleChanges, EventEmitter, signal, inject, computed, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BoundingBox } from '@newroom-connect/library/types';
import { GeometryHelper, TransformHelper, MathHelper, Rectangle, DomHelper } from '@newroom-connect/library/helpers';
import { AlignmentGuideDirective, DragDropDirective, DragPositionEvent, ResizeDirective, TransformEvent, CornerPosition, ResizeEvent, ResizeMode } from '@newroom-connect/library/directives';
import { IScreenCoords, IOverlayUIConfig } from '@newroom-connect/library/interfaces';

import { IconComponent } from '../icon/icon.component';
import { TooltipComponent } from '../tooltip/tooltip.component';
import { CoordinateValidationHelper } from '../cubemap-marzipano/helpers/coordinate-validation.helper';
import { ViewportHelper, IHoverState } from '../cubemap-marzipano/helpers/viewport.helper';

interface CornerHandle extends IScreenCoords {
  id: string;
}

@Component({
  selector: 'nrc-scene-overlay',
  standalone: true,
  imports: [CommonModule, AlignmentGuideDirective, DragDropDirective, ResizeDirective, IconComponent, TooltipComponent],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './scene-overlay.component.html',
  styleUrls: ['./scene-overlay.component.scss']
})
export class SceneOverlayComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  private static readonly MENU_POSITION_OFFSET = 8;

  // Z-index layer constants for proper stacking
  public readonly Z_INDEX_LAYER_SPACING = 5; // Space between each area element

  @Input({ required: true }) public id!: string;
  @Input({ required: true }) public boundingBox!: BoundingBox;
  @Input({ required: true }) public parentContainerRef!: ElementRef<HTMLElement>;

  @Output() public boundingBoxChanged = new EventEmitter<BoundingBox>();
  @Output() public lastIsResizableStatusEvent = new EventEmitter<{id: string, isResizable: boolean}>();

  @Input() public isEditMode = false;
  @Input() public isLocked = false;
  @Input() public isResizable = false;
  @Input() public keepAspectRatio = false;
  @Input() public zIndex = this.Z_INDEX_LAYER_SPACING;

  @ViewChild('targetElement', { static: true }) public targetElement!: ElementRef<HTMLDivElement>;
  @ViewChild('contentElement', { static: true }) public contentElement!: ElementRef<HTMLDivElement>;
  @ViewChild('overlayElement', { static: true }) public overlayElement!: ElementRef<HTMLDivElement>;
  @ViewChild('menuElement', { static: true }) public menuElement!: ElementRef<HTMLDivElement>;

  public currentMode = signal<ResizeMode>(ResizeMode.RESIZE);
  public aspectRatio = signal<number>(1);
  public cornerHandles = signal<CornerHandle[]>([]);
  public menuPosition = signal<IScreenCoords>({ x: 0, y: 0 });

  // Menu button hover states.
  public modeButtonHovered = signal<boolean>(false);
  public aspectButtonHovered = signal<boolean>(false);

  // UI config signal driving icon and border classes.
  public readonly overlayUiConfig = computed<IOverlayUIConfig>(() => {
    const hoverStates: IHoverState = {
      mode: this.modeButtonHovered(),
      aspect: this.aspectButtonHovered()
    };

    return ViewportHelper.calculateOverlayUIConfig(this.currentMode(), this.isAspectLocked(), hoverStates);
  });

  public activeResizeCorner = signal<CornerPosition | null>(null);
  public isAspectLocked = signal<boolean>(false);

  // Computed properties for internal state
  public showOverlay = signal<boolean>(false);

  public resizeMode = ResizeMode;
  public isDragging = signal<boolean>(false);

  // Internal state.
  private corners = signal<IScreenCoords[]>([]);
  private resizeObserver?: ResizeObserver;
  private isInitialized = signal<boolean>(false);
  private lastTargetRect?: Rectangle;

  public currentCornersForDirective = computed<TransformEvent['corners'] | undefined>(() => {
    const corners = this.corners();

    if (corners.length !== 4) {
      return undefined;
    }

    return {
      topLeft: { x: corners[0].x, y: corners[0].y },
      topRight: { x: corners[1].x, y: corners[1].y },
      bottomLeft: { x: corners[2].x, y: corners[2].y },
      bottomRight: { x: corners[3].x, y: corners[3].y }
    };
  });

  /**
   *
   */
  public ngOnInit(): void {
    this.cornerHandles.set([
      { id: 'tl', x: 0, y: 0 },
      { id: 'tr', x: 0, y: 0 },
      { id: 'bl', x: 0, y: 0 },
      { id: 'br', x: 0, y: 0 }
    ]);
  }

  /**
   *
   * @param changes
   */
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['boundingBox'] && !changes['boundingBox'].firstChange && !this.isDragging()) {
      this.refreshLayout();
    }

    // Update aspect lock when keepAspectRatio changes
    if (changes['keepAspectRatio']) {
      this.isAspectLocked.set(this.keepAspectRatio);
    }

    if (changes['isResizable'] || changes['isEditMode']) {
      this.showOverlay.set(this.isResizable && this.isEditMode);
    }
  }

  /**
   * Refresh the layout when bounding box is updated externally.
   * This is needed because Angular's change detection doesn't catch mutations to the same object reference.
   */
  public refreshLayout(): void {
    this.applyInitialLayout();

    // Use setTimeout to ensure DOM is updated before processing
    setTimeout(() => {
      // Re-evaluate mode based on current bounding box data
      this.currentMode.set(TransformHelper.hasPerspectiveData(this.boundingBox) ? ResizeMode.TRANSFORM : ResizeMode.RESIZE);

      if (this.currentMode() === ResizeMode.TRANSFORM) {
        // In transform mode, restore corners from perspective data
        this.restoreCornersFromPerspectiveData();
      } else {
        // Update corners from current element position
        this.updateCornersFromElement();
        this.positionOverlay();
      }
    }, 0);
  }

  /**
   *
   */
  public ngAfterViewInit(): void {
    this.mountElement();
    this.setupResizeObserver();

    this.isInitialized.set(true);
  }

  /**
   *
   */
  public ngOnDestroy(): void {
    if (!this.resizeObserver) {
      return;
    }

    this.resizeObserver.disconnect();
    this.isInitialized.set(false);
  }

  @HostListener('window:resize')
  public onWindowResize(): void {
    if (!this.isInitialized()) {
      return;
    }

    // Handle both resize and transform modes on window resize
    if (this.currentMode() === ResizeMode.TRANSFORM) {
      // In transform mode, just reposition the overlay handles
      // The nrcResize directive will handle corner scaling via onCornersScaled
      this.positionOverlayHandles();
    } else {
      // In resize mode, handle full parent resize logic
      this.handleParentResize();
    }
  }

  /**
   * Mount the element and set up initial positioning.
   */
  private mountElement(): void {
    if (!this.targetElement || !this.parentContainerRef) {
      return;
    }

    // Set initial dimensions and position from boundingBox
    this.applyInitialLayout();

    // Use setTimeout to ensure DOM is updated before positioning overlay
    setTimeout(() => {
      // Check if element has existing transform to determine initial mode
      this.currentMode.set(TransformHelper.hasPerspectiveData(this.boundingBox) ? ResizeMode.TRANSFORM : ResizeMode.RESIZE);

      if (this.currentMode() === ResizeMode.TRANSFORM) {
        // In transform mode, restore corners from perspective data for the directive
        this.restoreCornersFromPerspectiveData();
      } else {
        // Update corners from current element position
        this.updateCornersFromElement();
      }

      // Position overlay elements
      this.positionOverlay();
    }, 0);
  }

  /**
   * Apply initial layout from boundingBox.
   */
  private applyInitialLayout(): void {
    const parentRect = this.parentContainerRef.nativeElement.getBoundingClientRect();

    // Use GeometryHelper to apply layout
    GeometryHelper.applyLayoutFromBoundingBox(this.targetElement.nativeElement, this.boundingBox);

    // Calculate aspect ratio
    const width = (this.boundingBox.width / 100) * parentRect.width;
    const height = (this.boundingBox.height / 100) * parentRect.height;

    this.aspectRatio.set(MathHelper.calculateAspectRatio(width, height));
    // Snapshot current target rect for later delta calculations
    const rect = this.targetElement.nativeElement.getBoundingClientRect();

    this.lastTargetRect = { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
  }

  /**
   * Update corners from current element position.
   */
  private updateCornersFromElement(): void {
    const rect = this.targetElement.nativeElement.getBoundingClientRect();
    const elementRect: Rectangle = {
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height
    };

    // Use GeometryHelper to convert element rect to corners
    const cornerPositions = GeometryHelper.elementRectToCorners(elementRect);
    const screenCoords = TransformHelper.cornerPositionsToScreenCoords(cornerPositions);

    this.corners.set([...screenCoords]);
    this.lastTargetRect = { ...elementRect };
  }

  /**
   * Restore corners from perspective data for directive initialization.
   * This method only sets the corners - the directive handles transform application.
   */
  private restoreCornersFromPerspectiveData(): void {
    if (!TransformHelper.hasPerspectiveData(this.boundingBox)) {
      return;
    }

    // Get element and parent rectangles after layout has been applied
    const elementRect = this.targetElement.nativeElement.getBoundingClientRect();
    const parentRect = this.parentContainerRef.nativeElement.getBoundingClientRect();

    // Use TransformHelper to restore corners from perspective data
    const cornerPositions = TransformHelper.restoreCornersFromPerspective(
      this.boundingBox,
      elementRect,
      parentRect
    );

    // Convert to ScreenCoords array
    const newCorners = [
      { x: cornerPositions.topLeft.x, y: cornerPositions.topLeft.y },
      { x: cornerPositions.topRight.x, y: cornerPositions.topRight.y },
      { x: cornerPositions.bottomLeft.x, y: cornerPositions.bottomLeft.y },
      { x: cornerPositions.bottomRight.x, y: cornerPositions.bottomRight.y }
    ];

    // Validate corners before applying
    if (CoordinateValidationHelper.isValidCornerArray(newCorners)) {
      this.corners.set(newCorners);

      // Position overlay handles
      this.positionOverlayHandles();
    } else {
      // Fallback: use element bounds to create rectangular corners
      this.updateCornersFromElement();
      this.positionOverlay();
    }
  }

  /**
   * Handle scaled corners from the nrcResize directive on container resize.
   * This method only updates the visual state without emitting boundingBoxChanged events.
   *
   * @param scaled
   */
  public onCornersScaled(scaled: TransformEvent['corners']): void {
    this.corners.set([
      { x: scaled.topLeft.x, y: scaled.topLeft.y },
      { x: scaled.topRight.x, y: scaled.topRight.y },
      { x: scaled.bottomLeft.x, y: scaled.bottomLeft.y },
      { x: scaled.bottomRight.x, y: scaled.bottomRight.y }
    ]);

    // Update overlay handles/menu positions
    if (this.isEditMode && this.showOverlay()) {
      this.positionOverlayHandles();
    }

    // DO NOT emit boundingBoxChanged here - this is for container resize/external updates
    // Only user interactions should trigger boundingBoxChanged events
  }

  /**
   * Position overlay elements (handles and menu).
   */
  private positionOverlay(): void {
    const corners = this.corners();

    if (!this.targetElement) {
      return;
    }

    const targetBounds = ViewportHelper.getElementBounds(this.targetElement.nativeElement);

    // Use ViewportHelper to calculate positions
    const handlePositions = ViewportHelper.calculateOverlayHandlePositions(corners, targetBounds);
    const menuPos = ViewportHelper.calculateMenuPosition(corners, targetBounds, SceneOverlayComponent.MENU_POSITION_OFFSET);

    // Validate handle positions before applying
    if (handlePositions.length === 4) {
      this.cornerHandles.set(handlePositions);
      this.menuPosition.set(menuPos);

      this.changeDetectorRef.detectChanges();
    }
  }

  /**
   * Position only the overlay handles without recalculating corners.
   * Used during transform mode to avoid resetting the transform.
   */
  private positionOverlayHandles(): void {
    const corners = this.corners();

    if (!this.targetElement || !CoordinateValidationHelper.isValidCornerArray(corners)) {
      return;
    }

    const targetBounds = ViewportHelper.getElementBounds(this.targetElement.nativeElement);

    // Use ViewportHelper to calculate positions (same as positionOverlay but without recalculating corners)
    const handlePositions = ViewportHelper.calculateOverlayHandlePositions(corners, targetBounds);
    const menuPos = ViewportHelper.calculateMenuPosition(corners, targetBounds, SceneOverlayComponent.MENU_POSITION_OFFSET);

    this.cornerHandles.set(handlePositions);
    this.menuPosition.set(menuPos);
    this.changeDetectorRef.detectChanges();
  }

  /**
   * Set up resize observer for parent container.
   */
  private setupResizeObserver(): void {
    if (!this.parentContainerRef) {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => {
      if (this.currentMode() === ResizeMode.TRANSFORM) {
        // In transform mode, just reposition the overlay handles
        // The nrcResize directive handles corner scaling via onCornersScaled
        this.positionOverlayHandles();
      } else {
        // In resize mode, handle full parent resize logic
        this.handleParentResize();
      }
    });

    this.resizeObserver.observe(this.parentContainerRef.nativeElement);
  }

  /**
   * Handle parent container resize.
   */
  private handleParentResize(): void {
    if (this.currentMode() !== ResizeMode.TRANSFORM) {
      // In resize mode: apply layout first, then recalculate corners
      this.applyInitialLayout();
      this.updateCornersFromElement();
      this.positionOverlay();
    }

    this.updateBoundingBox();
  }

  /**
   * Toggle between resize and transform modes.
   */
  public toggleMode(): void {
    if (this.currentMode() === ResizeMode.TRANSFORM) {
      this.bakeTransformIntoLayout();
      this.currentMode.set(ResizeMode.RESIZE);
    } else {
      this.updateCornersFromElement();
      this.currentMode.set(ResizeMode.TRANSFORM);
    }

    // Update corners from current element position before positioning overlay
    this.updateCornersFromElement();
    this.positionOverlay();

    // Emit bounding box changes
    this.emitBoundingBoxChange();
  }

  /**
   * Bake transform into layout (convert from transform mode to resize mode).
   */
  private bakeTransformIntoLayout(): void {
    const corners = this.corners();

    if (corners.length !== 4) {
      return;
    }

    const parentRect = this.parentContainerRef.nativeElement.getBoundingClientRect();

    // Convert corners to corner positions format
    const cornerPositions = TransformHelper.screenCoordsToCornerPositions(corners.map(corner => ({ x: corner.x, y: corner.y })));

    // Use GeometryHelper to convert corners to element rectangle
    const elementRect = GeometryHelper.cornersToElementRect(cornerPositions);

    // Apply the new layout in batch
    DomHelper.setStyles(this.targetElement.nativeElement, {
      left: `${Math.round(elementRect.left - parentRect.left)}px`,
      top: `${Math.round(elementRect.top - parentRect.top)}px`,
      width: `${Math.round(elementRect.width)}px`,
      height: `${Math.round(elementRect.height)}px`
    });

    // Clear any transform
    this.contentElement.nativeElement.style.transform = '';

    // Clear all perspective data since we're switching to resize mode
    TransformHelper.clearPerspectiveData(this.boundingBox);

    // Update corners and aspect ratio
    this.updateCornersFromElement();
    this.aspectRatio.set(MathHelper.calculateAspectRatio(elementRect.width, elementRect.height) || this.aspectRatio());
  }

  /**
   * Get bounding box from corner coordinates.
   * @param corners - Array of corner coordinates.
   * @returns Bounding box dimensions and position.
   */
  private getBoundingBoxFromCorners(corners: IScreenCoords[]): { minX: number; maxX: number; minY: number; maxY: number; width: number; height: number } {
    const points = corners.map(corner => ({ x: corner.x, y: corner.y }));

    return GeometryHelper.getBoundingBoxFromPoints(points);
  }

  /**
   * Handle corner handle drag start.
   * @param event - Pointer event.
   * @param cornerIndex - Index of the corner being dragged.
   */
  public onCornerDragStart(event: PointerEvent, cornerIndex: number): void {
    if (!this.isEditMode) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.isDragging.set(true);
    // Delegate drag to nrcResize directive by setting active corner
    const mapIdxToId: Record<number, CornerPosition['id']> = { 0: 'topLeft', 1: 'topRight', 2: 'bottomLeft', 3: 'bottomRight' };
    const id = mapIdxToId[cornerIndex];

    this.activeResizeCorner.set({ id, x: event.clientX, y: event.clientY });
  }

  /**
   * Update corners from directive and position handles (matrix is applied by directive).
   *
   * @param event
   */
  public onDirectiveTransform(event: TransformEvent): void {
    const newCorners = [
      { x: event.corners.topLeft.x, y: event.corners.topLeft.y },
      { x: event.corners.topRight.x, y: event.corners.topRight.y },
      { x: event.corners.bottomLeft.x, y: event.corners.bottomLeft.y },
      { x: event.corners.bottomRight.x, y: event.corners.bottomRight.y }
    ];

    // Stop drag if transform is invalid
    if (ViewportHelper.shouldStopTransformDrag(newCorners)) {
      this.activeResizeCorner.set(null);
      return;
    }

    this.corners.set(newCorners);
    this.positionOverlayHandles();
  }

  /**
   * Update bounding box from directive-emitted percentages.
   *
   * @param event
   */
  public onDirectiveResize(event: ResizeEvent): void {
    this.boundingBox = {
      ...this.boundingBox,
      width: event.width,
      height: event.height,
      top: event.top,
      left: event.left
    };

    // Update element corners and overlay
    this.updateCornersFromElement();
    this.positionOverlay();

    this.boundingBoxChanged.emit({ ...this.boundingBox });
  }

  /**
   * Handle drag end from directive.
   */
  public onDirectiveDragEnded(): void {
    this.isDragging.set(false);
    // Clear active corner to stop directive drag
    this.activeResizeCorner.set(null);

    if (this.currentMode() === ResizeMode.TRANSFORM) {
      this.updatePerspectiveDataAndEmitPatch();
      this.positionOverlayHandles();
    } else {
      this.updateCornersFromElement();
      this.positionOverlay();
      this.emitBoundingBoxChange();
    }
  }

  /**
   * Update bounding box from current element state.
   */
  private updateBoundingBox(): void {
    const parentRect = this.parentContainerRef.nativeElement.getBoundingClientRect();

    if (this.currentMode() === ResizeMode.TRANSFORM && this.corners().length === 4) {
      // In transform mode, use TransformHelper to update with transform data
      const cornerPositions = TransformHelper.screenCoordsToCornerPositions(
        this.corners().map(corner => ({ x: corner.x, y: corner.y }))
      );

      const elementRect = this.targetElement.nativeElement.getBoundingClientRect();
      const transformMatrix = this.contentElement.nativeElement.style.transform;

      TransformHelper.updateBoundingBoxWithTransformData(this.boundingBox, cornerPositions, parentRect, elementRect, transformMatrix);

      // Update aspect ratio
      const bbox = this.getBoundingBoxFromCorners(this.corners());

      this.aspectRatio.set(MathHelper.calculateAspectRatio(bbox.width, bbox.height) || this.aspectRatio());
    } else {
      // In resize mode, use TransformHelper to update with resize data
      const elementRect = this.targetElement.nativeElement.getBoundingClientRect();

      TransformHelper.updateBoundingBoxWithResizeData(this.boundingBox, elementRect, parentRect);

      // Update aspect ratio
      this.aspectRatio.set(MathHelper.calculateAspectRatio(elementRect.width, elementRect.height) || this.aspectRatio());
    }
  }

  /**
   * Emit bounding box changes to parent component.
   */
  private emitBoundingBoxChange(): void {
    this.updateBoundingBox();

    this.boundingBoxChanged.emit({ ...this.boundingBox });
  }

  /**
   * Update perspective data and emit patch bounding box.
   * This preserves existing spherical and layout data while updating only perspective information.
   */
  private updatePerspectiveDataAndEmitPatch(): void {
    if (this.corners().length !== 4) {
      return;
    }

    // Get element's current size for normalized coordinates
    const elementRect = this.targetElement.nativeElement.getBoundingClientRect();
    const currentMatrix = this.contentElement.nativeElement.style.transform;

    // Convert corners to CornerPositions format using TransformHelper
    const cornerPositions = TransformHelper.screenCoordsToCornerPositions(this.corners().map(corner => ({ x: corner.x, y: corner.y })));

    // Use TransformHelper to update perspective data
    this.boundingBox.perspective = TransformHelper.updatePerspectiveData(this.boundingBox, cornerPositions, elementRect, currentMatrix);

    // Emit patch bounding box
    const patchBoundingBox = TransformHelper.createPerspectivePatch(this.boundingBox);

    this.boundingBoxChanged.emit(patchBoundingBox);
  }

  /**
   * Handle drag position changes from the drag-and-drop directive.
   * @param event - Drag position event with new top and left percentages.
   */
  public onDragPositionChange(event: DragPositionEvent): void {
    // Update the bounding box with new position
    this.boundingBox = { ...this.boundingBox, top: event.top, left: event.left };

    // Calculate delta movement of the element (in pixels)
    const prevRect = this.lastTargetRect;
    const newRectDom = this.targetElement.nativeElement.getBoundingClientRect();
    const newRect: Rectangle = { left: newRectDom.left, top: newRectDom.top, width: newRectDom.width, height: newRectDom.height };

    if (this.currentMode() === ResizeMode.TRANSFORM) {
      // Shift corners by the element's movement delta so handles and transform stay aligned
      if (prevRect && this.corners().length === 4) {
        const deltaX = newRect.left - prevRect.left;
        const deltaY = newRect.top - prevRect.top;
        const shifted = this.corners().map(corner => ({ x: corner.x + deltaX, y: corner.y + deltaY }));

        this.corners.set(shifted);
      }

      this.positionOverlayHandles();
      this.boundingBoxChanged.emit({ ...this.boundingBox });
      this.lastTargetRect = newRect;

      return;
    }

    // In resize mode, update corners from element position
    this.updateCornersFromElement();
    this.positionOverlay();
    this.boundingBoxChanged.emit({ ...this.boundingBox });
    this.lastTargetRect = newRect;
  }
}
